import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';

export interface ScrollRevealProps {
    children: ReactNode;
    /** Extra classes on the wrapper (it is a full-width block). */
    className?: string;
    /** Transition delay in ms, for staggering sibling reveals. */
    delay?: number;
}

/**
 * Fades + slides a section up as it scrolls into view (desktop scroll-reveal).
 * Uses a one-shot IntersectionObserver + GPU transform/opacity transition, so
 * it's cheap and never re-triggers. Honours `prefers-reduced-motion` by showing
 * the content immediately with no movement. SSR renders the hidden state; the
 * observer reveals on hydration (in-view sections reveal on first paint).
 */
export const ScrollReveal = ({
    children,
    className,
    delay,
}: ScrollRevealProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const prefersReduced = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (prefersReduced) {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: '0px 0px -10% 0px' },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={cn(
                'w-full transform-gpu transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none',
                visible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0',
                className,
            )}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    );
};
